import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight, Clock3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";

const NEW_SITE_URL = "https://fcyit.com";
const REDIRECT_SECONDS = 10;

export default function MoveNotice() {
  const [secondsRemaining, setSecondsRemaining] = useState(REDIRECT_SECONDS);
  const reduceMotion = useReducedMotion();
  const goNowRef = useRef(null);

  useEffect(() => {
    goNowRef.current?.focus({ preventScroll: true });
    const previousTitle = document.title;
    document.title = "We've moved to fcyit.com | FCY IT Consultancy";
    const deadline = Date.now() + REDIRECT_SECONDS * 1000;
    setSecondsRemaining(REDIRECT_SECONDS);

    const countdown = window.setInterval(() => {
      setSecondsRemaining(Math.max(0, Math.ceil((deadline - Date.now()) / 1000)));
    }, 1000);
    const redirect = window.setTimeout(() => {
      window.clearInterval(countdown);
      setSecondsRemaining(0);
      // Keep navigation outside state updaters, which React may run twice.
      // Replace avoids sending Back straight into another timed redirect.
      try {
        window.location.replace(NEW_SITE_URL);
      } catch {
        // The native links remain available if automatic navigation is blocked.
      }
    }, REDIRECT_SECONDS * 1000);

    return () => {
      window.clearInterval(countdown);
      window.clearTimeout(redirect);
      document.title = previousTitle;
    };
  }, []);

  return (
    <main
      className="min-h-screen min-h-svh bg-background text-foreground flex items-center justify-center px-4 py-10 sm:px-6"
      data-testid="move-notice-root"
    >
      <motion.div
        className="w-full max-w-xl lg:max-w-2xl"
        initial={reduceMotion ? false : { opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: reduceMotion ? 0 : 0.2 }}
      >
        <Card className="rounded-2xl border-border bg-card/80 p-6 text-left backdrop-blur-xl sm:p-10">
          <div className="flex items-center gap-3 border-b border-border pb-6">
            <img
              src="/fcy-mark.png"
              alt="FCY logo"
              className="h-8 w-8 object-contain"
              data-testid="move-notice-logo"
            />
            <span className="text-xs font-semibold tracking-widest sm:text-sm" data-testid="move-notice-company">
              FCY IT CONSULTANCY LTD
            </span>
          </div>

          <div className="py-8 sm:py-10">
            <p className="mb-3 text-xs font-medium uppercase tracking-widest text-primary" data-testid="move-notice-label">
              A new address. The same commitment.
            </p>
            <h1 className="text-4xl font-semibold tracking-tight sm:text-5xl" data-testid="move-notice-heading">
              We’ve moved.
            </h1>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground" data-testid="move-notice-description">
              FCY IT Consultancy is now on our new website.
              You’ll find us at:
            </p>
            <p className="mt-4 text-3xl font-semibold tracking-tight text-primary sm:text-4xl" data-testid="move-notice-destination">
              fcyit.com
            </p>
          </div>

          <div className="space-y-3">
            <p
              className="flex items-center gap-2 text-sm text-muted-foreground tabular-nums"
              role="status"
              aria-live="polite"
              aria-atomic="true"
              data-testid="move-notice-countdown"
            >
              <Clock3 className="h-4 w-4 shrink-0" aria-hidden="true" />
              {secondsRemaining > 0
                ? `Taking you there in ${secondsRemaining} ${secondsRemaining === 1 ? "second" : "seconds"}…`
                : "Not redirected? Use the button below to continue."}
            </p>
            <Progress
              value={((REDIRECT_SECONDS - secondsRemaining) / REDIRECT_SECONDS) * 100}
              aria-label="Time until redirect"
              aria-valuetext={`${secondsRemaining} seconds remaining`}
              className="h-1 [&>div]:transition-transform [&>div]:duration-200 motion-reduce:[&>div]:transition-none"
              data-testid="move-notice-progress"
            />
          </div>

          <motion.div className="mt-6" whileTap={reduceMotion ? undefined : { scale: 0.98 }}>
            <Button
              asChild
              size="lg"
              className="h-12 w-full rounded-xl text-base font-semibold transition-transform duration-200 hover:-translate-y-0.5 active:scale-95 motion-reduce:transform-none motion-reduce:transition-none sm:w-auto"
            >
              <a ref={goNowRef} href={NEW_SITE_URL} data-testid="move-notice-go-now-button">
                Go to fcyit.com now
                <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
              </a>
            </Button>
          </motion.div>
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground" data-testid="move-notice-fallback-text">
            If you’re not redirected automatically, use the button above or{" "}
            <a
              href={NEW_SITE_URL}
              className="inline-flex min-h-11 items-center rounded-sm text-primary underline underline-offset-4 hover:text-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:opacity-80"
              data-testid="move-notice-new-site-link"
            >
              click here
            </a>.
          </p>
        </Card>
      </motion.div>
    </main>
  );
}
